import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Building2,
  Radio,
  Wifi,
  Server,
  Shield,
  CheckCircle,
  ArrowRight,
  Globe,
  Landmark,
  Factory,
  Car,
  Wind,
  Droplets,
  Trash2,
  Lightbulb,
  MapPin,
  BarChart3,
  TrendingUp,
  Users,
  Zap,
  Layers,
  Network,
  Database,
  Settings,
  Target,
  Rocket,
  GraduationCap,
  Briefcase,
  Truck,
  Leaf
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/Header';
import { TopBar } from '@/components/TopBar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/sections/ContactForm';
import { HreflangTags } from '@/components/HreflangTags';
import { useLanguage } from '@/i18n/LanguageContext';

// Custom generated images
const IMAGES = {
  hero: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/519bef3ff71e411778a4a84f6a5f40ff07f114f9fd7e15a4d65431ee2dd48499.png",
  architecture: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/8713637ef3b95f3271252d1a4669704024a1e15c4cb152296fe60c0110cbbb87.png",
  fragmentedVsUnified: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/ceecac085416e266c6e6988b7b8fc6531a21a36e60c95431a6d1b9eac1a3d98c.png",
  multiService: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/fb1f0a5a3843b2dd8a9a78f80de5ae3f7988dafdc9c0203163ceb424b0dab578.png",
  innovationEcosystem: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/ce2793c63c7ab35cc51562d8c16e058bf42104b59ec5b4b4444696b2382d6bc4.png",
  dashboard: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/c9313b388ef3bb2f90b1e53c39b7f129201d990c648d625217bfe93083c9508c.png"
};

const SmartCityInfrastructurePage = () => {
  const { t } = useLanguage();
  
  return (
    <>
      <Helmet>
        <title>{t('smartCityPage.metaTitle')}</title>
        <meta name="description" content={t('smartCityPage.metaDesc')} />
        <meta name="keywords" content="smart city infrastructure, smart city IoT infrastructure, urban IoT networks, LoRaWAN smart city network, smart city connectivity, city digital infrastructure, LPWAN smart city, smart city platform, urban IoT connectivity" />
        <link rel="canonical" href="https://0gbaltics.com/smart-city-iot-infrastructure" />
        <meta property="og:title" content={t('smartCityPage.metaTitle')} />
        <meta property="og:description" content={t('smartCityPage.metaDesc')} />
        <meta property="og:type" content="website" />
      </Helmet>
      <HreflangTags />

      <TopBar />
      <Header />

      <main className="pt-20">
        
        {/* ========================================= */}
        {/* HERO SECTION */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="smart-city-hero">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                  {t('smartCityPage.heroBadge')}
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  {t('smartCityPage.heroTitle')}
                </h1>
                <p className="text-xl text-slate-300 mb-6 leading-relaxed">
                  {t('smartCityPage.heroSubtitle')}
                </p>
                <div className="flex flex-wrap gap-4 mb-8">
                  <Button 
                    size="lg"
                    className="bg-teal-500 hover:bg-teal-600 text-white"
                    onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                    data-testid="hero-cta"
                  >
                    {t('smartCityPage.ctaButton')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
                
                {/* Technology badges */}
                <div className="flex flex-wrap gap-3">
                  {['LoRaWAN', 'NB-IoT', 'Smart Parking', 'Air Quality', 'Metering', 'Asset Tracking'].map((tech, i) => (
                    <span key={i} className="bg-slate-800 border border-slate-700 text-slate-300 text-sm px-3 py-1 rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="relative">
                <img 
                  src={IMAGES.hero}
                  alt="Smart City IoT Infrastructure - urban network with gateways, sensors and city dashboard"
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* WHAT IS SMART CITY INFRASTRUCTURE */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="what-is-infrastructure">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('smartCityPage.whatIsBadge') || 'Understanding the Foundation'}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('smartCityPage.whatIsTitle') || 'What is Smart City IoT Infrastructure?'}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('smartCityPage.whatIsDesc') || 'Smart city infrastructure is the digital backbone of connected urban services.'}
                </p>
                
                <h4 className="font-semibold text-slate-900 mb-4">{t('smartCityPage.dataCollected') || 'Urban data collected includes:'}</h4>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: Car, text: t('smartCityPage.traffic') || 'Traffic patterns' },
                    { icon: Wind, text: t('smartCityPage.airQuality') || 'Air quality' },
                    { icon: MapPin, text: t('smartCityPage.parking') || 'Parking availability' },
                    { icon: Droplets, text: t('smartCityPage.water') || 'Water consumption' },
                    { icon: Zap, text: t('smartCityPage.energy') || 'Energy usage' },
                    { icon: Building2, text: t('smartCityPage.assets') || 'Public assets' },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <Icon className="h-5 w-5 text-teal-600" />
                        <span className="text-slate-700">{item.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div>
                <img 
                  src={IMAGES.architecture}
                  alt="Smart city IoT architecture - sensors to network to gateways to platform to dashboard"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* WHY PROJECTS FAIL */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="why-projects-fail">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="order-2 lg:order-1">
                <img 
                  src={IMAGES.fragmentedVsUnified}
                  alt="Fragmented pilot projects vs unified smart city infrastructure"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
              
              <div className="order-1 lg:order-2">
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-red-400 mb-4">
                  {t('smartCityPage.failBadge') || 'Common Challenges'}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                  {t('smartCityPage.failTitle') || 'Why Many Smart City Projects Fail'}
                </h2>
                <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                  {t('smartCityPage.failDesc') || 'Many smart city initiatives begin with isolated pilot projects without building a long-term infrastructure strategy.'}
                </p>
                
                <h4 className="font-semibold text-white mb-4">{t('smartCityPage.commonProblems') || 'Common problems include:'}</h4>
                <div className="space-y-3 mb-8">
                  {[
                    { icon: Network, text: t('smartCityPage.problem1') || 'Fragmented connectivity with multiple incompatible systems' },
                    { icon: Server, text: t('smartCityPage.problem2') || 'Unreliable networks that cannot support growing device counts' },
                    { icon: TrendingUp, text: t('smartCityPage.problem3') || 'Pilot projects that cannot scale to city-wide deployment' },
                    { icon: Database, text: t('smartCityPage.problem4') || 'Data silos that prevent unified city intelligence' },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon className="h-4 w-4 text-red-400" />
                        </div>
                        <span className="text-slate-300">{item.text}</span>
                      </div>
                    );
                  })}
                </div>
                
                <div className="bg-teal-600/20 border border-teal-500/30 p-6 rounded-lg">
                  <p className="text-teal-300">
                    <strong>{t('smartCityPage.solutionText')}</strong> {t('smartCityPage.solutionDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* THE FOUNDATION: CONNECTIVITY */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="connectivity-foundation">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('smartCityPage.connectivityBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('smartCityPage.connectivityTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('smartCityPage.connectivityDesc')}
              </p>
            </div>

            <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6 mb-12">
              {[
                { icon: Radio, name: t('smartCityPage.lorawanName'), desc: t('smartCityPage.lorawanDesc'), color: 'bg-blue-600' },
                { icon: Wifi, name: t('smartCityPage.nbiotName'), desc: t('smartCityPage.nbiotDesc'), color: 'bg-green-600' },
                { icon: Wifi, name: t('smartCityPage.ltemName'), desc: t('smartCityPage.ltemDesc'), color: 'bg-purple-600' },
                { icon: Database, name: t('smartCityPage.bleName'), desc: t('smartCityPage.bleDesc'), color: 'bg-orange-500' },
                { icon: Globe, name: t('smartCityPage.satelliteName'), desc: t('smartCityPage.satelliteDesc'), color: 'bg-cyan-600' },
              ].map((tech, i) => {
                const Icon = tech.icon;
                return (
                  <div key={i} className="bg-slate-50 border border-slate-200 p-6 rounded-lg text-center">
                    <div className={`w-12 h-12 ${tech.color} rounded-lg flex items-center justify-center mx-auto mb-4`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-2">{tech.name}</h4>
                    <p className="text-slate-600 text-sm">{tech.desc}</p>
                  </div>
                );
              })}
            </div>

            <div className="bg-teal-50 border border-teal-200 p-6 rounded-lg max-w-3xl mx-auto">
              <p className="text-teal-800 text-center">
                {t('smartCityPage.connectivityNote')}
              </p>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* CARRIER GRADE NETWORKS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="carrier-grade">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('smartCityPage.carrierBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('smartCityPage.carrierTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('smartCityPage.carrierDesc1')}
                </p>
                <p className="text-slate-600 mb-8 leading-relaxed">
                  {t('smartCityPage.carrierDesc2')}
                </p>
                
                <h4 className="font-semibold text-slate-900 mb-4">{t('smartCityPage.carrierProvide')}</h4>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: Shield, title: t('smartCityPage.highAvailability'), desc: t('smartCityPage.highAvailabilityDesc') },
                    { icon: Server, title: t('smartCityPage.secureCommunications'), desc: t('smartCityPage.secureCommunicationsDesc') },
                    { icon: Layers, title: t('smartCityPage.scalableArchitecture'), desc: t('smartCityPage.scalableArchitectureDesc') },
                    { icon: Settings, title: t('smartCityPage.professionalMonitoring'), desc: t('smartCityPage.professionalMonitoringDesc') },
                    { icon: Target, title: t('smartCityPage.longTermReliability'), desc: t('smartCityPage.longTermReliabilityDesc') },
                    { icon: Database, title: t('smartCityPage.redundancy'), desc: t('smartCityPage.redundancyDesc') },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="bg-white p-4 rounded-lg border border-slate-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="h-5 w-5 text-teal-600" />
                          <h5 className="font-semibold text-slate-900 text-sm">{item.title}</h5>
                        </div>
                        <p className="text-slate-600 text-xs">{item.desc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="bg-slate-900 p-8 rounded-lg">
                <h4 className="text-white font-semibold mb-6 text-lg">{t('smartCityPage.infrastructureLayers')}</h4>
                <div className="space-y-4">
                  {[
                    { layer: t('smartCityPage.layer5'), name: t('smartCityPage.layer5Name'), desc: t('smartCityPage.layer5Desc') },
                    { layer: t('smartCityPage.layer4'), name: t('smartCityPage.layer4Name'), desc: t('smartCityPage.layer4Desc') },
                    { layer: t('smartCityPage.layer3'), name: t('smartCityPage.layer3Name'), desc: t('smartCityPage.layer3Desc') },
                    { layer: t('smartCityPage.layer2'), name: t('smartCityPage.layer2Name'), desc: t('smartCityPage.layer2Desc') },
                    { layer: t('smartCityPage.layer1'), name: t('smartCityPage.layer1Name'), desc: t('smartCityPage.layer1Desc') },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-4 bg-slate-800 p-4 rounded-lg">
                      <div className="bg-teal-600 text-white text-xs font-bold px-2 py-1 rounded">
                        {item.layer}
                      </div>
                      <div>
                        <h5 className="text-white font-medium text-sm">{item.name}</h5>
                        <p className="text-slate-400 text-xs">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* LORAWAN FOR CITIES */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="lorawan-cities">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('smartCityPage.lorawanBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('smartCityPage.lorawanTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('smartCityPage.lorawanMainDesc')}
                </p>
                
                <div className="space-y-4 mb-8">
                  {[
                    { title: t('smartCityPage.lorawanFeature1Title'), desc: t('smartCityPage.lorawanFeature1Desc') },
                    { title: t('smartCityPage.lorawanFeature2Title'), desc: t('smartCityPage.lorawanFeature2Desc') },
                    { title: t('smartCityPage.lorawanFeature3Title'), desc: t('smartCityPage.lorawanFeature3Desc') },
                    { title: t('smartCityPage.lorawanFeature4Title'), desc: t('smartCityPage.lorawanFeature4Desc') },
                  ].map((item, i) => (
                    <div key={i} className="bg-slate-50 border border-slate-200 p-4 rounded-lg">
                      <h4 className="font-semibold text-slate-900 mb-1">{item.title}</h4>
                      <p className="text-slate-600 text-sm">{item.desc}</p>
                    </div>
                  ))}
                </div>
                
                <div className="bg-teal-50 border border-teal-200 p-6 rounded-lg">
                  <p className="text-teal-800">
                    {t('smartCityPage.lorawanNote')}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  { stat: t('smartCityPage.stat1Value'), label: t('smartCityPage.stat1Label') },
                  { stat: t('smartCityPage.stat2Value'), label: t('smartCityPage.stat2Label') },
                  { stat: t('smartCityPage.stat3Value'), label: t('smartCityPage.stat3Label') },
                  { stat: t('smartCityPage.stat4Value'), label: t('smartCityPage.stat4Label') },
                ].map((item, i) => (
                  <div key={i} className="bg-slate-900 p-6 rounded-lg text-center">
                    <div className="text-3xl font-bold text-teal-400 mb-2">{item.stat}</div>
                    <p className="text-slate-400 text-sm">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* MULTI-SERVICE INFRASTRUCTURE */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="multi-service">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                  {t('smartCityPage.multiServiceBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                  {t('smartCityPage.multiServiceTitle')}
                </h2>
                <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                  {t('smartCityPage.multiServiceDesc')}
                </p>
                
                <h4 className="font-semibold text-white mb-4">{t('smartCityPage.servicesShare')}</h4>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: Car, text: t('smartCityPage.smartParking') },
                    { icon: Wind, text: t('smartCityPage.airQualityMonitoring') },
                    { icon: Droplets, text: t('smartCityPage.smartWaterMetering') },
                    { icon: Trash2, text: t('smartCityPage.wasteManagementService') },
                    { icon: Truck, text: t('smartCityPage.municipalAssetTracking') },
                    { icon: Lightbulb, text: t('smartCityPage.smartStreetLighting') },
                    { icon: Building2, text: t('smartCityPage.smartBuildingMonitoring') },
                    { icon: Zap, text: t('smartCityPage.energyManagement') },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-center gap-2 bg-slate-800 p-3 rounded-lg">
                        <Icon className="h-5 w-5 text-teal-400" />
                        <span className="text-slate-300 text-sm">{item.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="flex justify-center">
                <img 
                  src={IMAGES.multiService}
                  alt="One IoT network supporting multiple smart city services"
                  className="w-full max-w-md h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* INNOVATION ECOSYSTEM */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="innovation-ecosystem">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('smartCityPage.innovationBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('smartCityPage.innovationTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('smartCityPage.innovationDesc')}
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <img 
                  src={IMAGES.innovationEcosystem}
                  alt="LPWAN innovation ecosystem with startups, universities, and services"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-6">{t('smartCityPage.organizationsTitle')}</h3>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {[
                    { icon: Rocket, label: t('smartCityPage.localStartups') },
                    { icon: Briefcase, label: t('smartCityPage.techCompanies') },
                    { icon: GraduationCap, label: t('smartCityPage.universities') },
                    { icon: Factory, label: t('smartCityPage.researchLabs') },
                    { icon: Landmark, label: t('smartCityPage.publicServiceProviders') },
                    { icon: Building2, label: t('smartCityPage.privateOperators') },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg">
                        <Icon className="h-5 w-5 text-teal-600" />
                        <span className="text-slate-700 text-sm">{item.label}</span>
                      </div>
                    );
                  })}
                </div>
                
                <p className="text-slate-600 mb-6">
                  {t('smartCityPage.innovationBarrier')}
                </p>
                
                <div className="bg-teal-50 border border-teal-200 p-4 rounded-lg">
                  <p className="text-teal-800 text-sm">
                    {t('smartCityPage.innovationAnalogy')}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-900 p-8 rounded-lg">
              <h4 className="text-white font-semibold mb-6 text-center">{t('smartCityPage.newServicesTitle')}</h4>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { icon: Truck, title: t('smartCityPage.smartLogistics'), desc: t('smartCityPage.smartLogisticsDesc') },
                  { icon: Leaf, title: t('smartCityPage.envMonitoringStartups'), desc: t('smartCityPage.envMonitoringStartupsDesc') },
                  { icon: Factory, title: t('smartCityPage.equipmentTracking'), desc: t('smartCityPage.equipmentTrackingDesc') },
                  { icon: Car, title: t('smartCityPage.sharedMobility'), desc: t('smartCityPage.sharedMobilityDesc') },
                  { icon: Zap, title: t('smartCityPage.energyOptimization'), desc: t('smartCityPage.energyOptimizationDesc') },
                  { icon: Building2, title: t('smartCityPage.smartBuildingProviders'), desc: t('smartCityPage.smartBuildingProvidersDesc') },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="bg-slate-800 p-4 rounded-lg">
                      <Icon className="h-6 w-6 text-teal-400 mb-2" />
                      <h5 className="text-white font-medium text-sm mb-1">{item.title}</h5>
                      <p className="text-slate-400 text-xs">{item.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* DATA AND CITY INTELLIGENCE */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="city-intelligence">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('smartCityPage.intelligenceBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('smartCityPage.intelligenceTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('smartCityPage.intelligenceDesc')}
                </p>
                
                <h4 className="font-semibold text-slate-900 mb-4">{t('smartCityPage.dataHelps')}</h4>
                <div className="space-y-3 mb-8">
                  {[
                    { icon: Car, text: t('smartCityPage.trafficPatterns') },
                    { icon: Wind, text: t('smartCityPage.envConditions') },
                    { icon: Building2, text: t('smartCityPage.infrastructureUsage') },
                    { icon: Zap, text: t('smartCityPage.energyConsumption') },
                    { icon: MapPin, text: t('smartCityPage.assetUtilization') },
                    { icon: TrendingUp, text: t('smartCityPage.urbanTrends') },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <Icon className="h-5 w-5 text-teal-600" />
                        <span className="text-slate-700">{item.text}</span>
                      </div>
                    );
                  })}
                </div>
                
                <p className="text-slate-600">
                  {t('smartCityPage.intelligenceConclusion')}
                </p>
              </div>
              
              <div>
                <img 
                  src={IMAGES.dashboard}
                  alt="Smart city dashboard showing maps, data graphs, and urban analytics"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* DEPLOYMENT STRATEGY */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="deployment-strategy">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('smartCityPage.deploymentBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('smartCityPage.deploymentTitle')}
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  {t('smartCityPage.deploymentDesc')}
                </p>
              </div>

              <div className="space-y-6">
                {[
                  {
                    step: t('smartCityPage.step01'),
                    title: t('smartCityPage.step01Title'),
                    desc: t('smartCityPage.step01Desc'),
                    color: 'bg-blue-600'
                  },
                  {
                    step: t('smartCityPage.step02'),
                    title: t('smartCityPage.step02Title'),
                    desc: t('smartCityPage.step02Desc'),
                    color: 'bg-teal-600'
                  },
                  {
                    step: t('smartCityPage.step03'),
                    title: t('smartCityPage.step03Title'),
                    desc: t('smartCityPage.step03Desc'),
                    color: 'bg-purple-600'
                  },
                  {
                    step: t('smartCityPage.step04'),
                    title: t('smartCityPage.step04Title'),
                    desc: t('smartCityPage.step04Desc'),
                    color: 'bg-orange-500'
                  },
                ].map((item, i) => (
                  <div key={i} className="flex gap-6 items-start">
                    <div className={`${item.color} text-white font-bold text-lg px-4 py-2 rounded-lg flex-shrink-0`}>
                      {item.step}
                    </div>
                    <div className="bg-slate-50 p-6 rounded-lg flex-grow border border-slate-200">
                      <h4 className="font-semibold text-slate-900 mb-2">{item.title}</h4>
                      <p className="text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* WHY 0G BALTICS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="why-0g-baltics">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                  {t('smartCityPage.whyBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                  {t('smartCityPage.whyTitle')}
                </h2>
                <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                  {t('smartCityPage.whyDesc')}
                </p>
                
                <div className="space-y-4 mb-8">
                  {[
                    { title: t('smartCityPage.service1Title'), desc: t('smartCityPage.service1Desc') },
                    { title: t('smartCityPage.service2Title'), desc: t('smartCityPage.service2Desc') },
                    { title: t('smartCityPage.service3Title'), desc: t('smartCityPage.service3Desc') },
                    { title: t('smartCityPage.service4Title'), desc: t('smartCityPage.service4Desc') },
                    { title: t('smartCityPage.service5Title'), desc: t('smartCityPage.service5Desc') },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <CheckCircle className="h-5 w-5 text-teal-400 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold text-white">{item.title}</h4>
                        <p className="text-slate-400 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-slate-800 p-8 rounded-lg border border-slate-700">
                  <h4 className="text-white font-semibold mb-6 text-lg">{t('smartCityPage.relatedSolutions')}</h4>
                  <div className="space-y-4">
                    {[
                      { to: '/connectivity', label: t('smartCityPage.relatedConnectivity'), desc: t('smartCityPage.relatedConnectivityDesc') },
                      { to: '/tektelic', label: t('smartCityPage.relatedTektelic'), desc: t('smartCityPage.relatedTektelicDesc') },
                      { to: '/smart-metering', label: t('smartCityPage.relatedMetering'), desc: t('smartCityPage.relatedMeteringDesc') },
                      { to: '/asset-tracking', label: t('smartCityPage.relatedAssetTracking'), desc: t('smartCityPage.relatedAssetTrackingDesc') },
                      { to: '/indoor-air-quality-monitoring', label: t('smartCityPage.relatedAirQuality'), desc: t('smartCityPage.relatedAirQualityDesc') },
                    ].map((link, i) => (
                      <Link key={i} to={link.to} className="block bg-slate-700 p-4 rounded-lg hover:bg-slate-600 transition-colors group">
                        <div className="flex items-center justify-between">
                          <div>
                            <h5 className="text-white font-medium">{link.label}</h5>
                            <p className="text-slate-400 text-sm">{link.desc}</p>
                          </div>
                          <ArrowRight className="h-5 w-5 text-teal-400 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* CTA SECTION */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-teal-600" data-testid="cta-section">
          <div className="container-0g text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('smartCityPage.ctaTitle')}
            </h2>
            <p className="text-xl text-teal-100 mb-8 max-w-3xl mx-auto">
              {t('smartCityPage.ctaDesc')}
            </p>
            <Button 
              size="lg"
              className="bg-white text-teal-700 hover:bg-teal-50 font-semibold"
              onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="cta-button"
            >
              {t('smartCityPage.ctaButton')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* ========================================= */}
        {/* CONTACT FORM */}
        {/* ========================================= */}
        <div id="contact-form">
          <ContactForm />
        </div>

      </main>

      <Footer />
    </>
  );
};

export default SmartCityInfrastructurePage;
